/**
 * Customer communication — project-first reply style, templates, preview and
 * test send (namespace `customerCommunication`). One fragment owned by this
 * area; `customerCommunicationNl` is typed against the English, so a missing
 * Dutch key fails typecheck exactly as it does in the main dictionary.
 */
export const customerCommunicationEn = {
  customerCommunication: {
    title: "Customer communication",
    subtitle: "How replies to customers sound, look and are signed — on every channel.",
    orgTitle: "Customer communication defaults",
    orgSubtitle: "The style every project uses unless it has its own. Projects that customize keep their own copy.",
    loading: "Loading customer communication…",
    loadError: "Customer communication settings could not be loaded.",
    retry: "Try again",
    tabs: {
      label: "Customer communication sections",
      style: "Style & sender",
      templates: "Templates",
      preview: "Preview & test",
      sending: "Sending & approval",
    },
    sending: {
      inherit: "Same as the organization",
      inheritValue: "Same as the organization ({value})",
      saved: "Saved.",
      saveError: "Saving failed. Try again.",
      saveBar: "Unsaved changes to sending & approval",
      readOnly: "Only Project Admins can change this.",
      orgHint: "Projects follow these settings unless they choose their own on their Approvals & rules page.",
      summaryLine: { replies: "Replies:", approval: "Approval:", manage: "Set up in Approvals & rules" },
      ai: {
        legend: "When may the AI send a reply by itself?",
        help: "What the AI does with an incoming customer e-mail in the inbox.",
        off: { title: "Off", body: "The AI does nothing with incoming e-mail." },
        suggest_only: { title: "Suggestions only", body: "The AI proposes a reply in the inbox. A person writes and sends." },
        auto_draft: { title: "Prepare drafts", body: "The AI puts a draft reply ready. A person checks it and sends it." },
        auto_send_low_risk: { title: "Send automatically at low risk", body: "The AI sends the reply itself when it is sure enough and the risk is low, within the limits below. Everything else becomes a draft." },
      },
      limits: {
        legend: "Limits for automatic sending",
        help: "Outside these limits the AI does not send: the reply becomes a draft a person checks.",
        confidence: { label: "Minimum certainty", help: "How sure the AI must be of its answer before it sends it by itself." },
        kb: { label: "Knowledge-base source required", help: "Only send by itself when the answer is backed by an article from the knowledge base." },
        max: { label: "Automatic replies per conversation per day", help: "After this, every further reply in that conversation becomes a draft." },
        yes: "Yes",
        no: "No",
        never: "The AI only answers questions and how-to requests by itself. Complaints, bugs, feature requests, refunds, billing disputes, legal and security questions, angry customers and anyone who asks for a person always get a draft.",
      },
      approval: {
        legend: "Must a reply be approved before it goes out?",
        help: "An approval stops the reply until an admin or manager approves it: in FixControl, from the e-mail they get, or in the project's Slack or Teams approvals channel when it has one.",
        off: { title: "No", body: "Whoever writes the reply sends it with one click. Nothing is sent automatically unless the AI setting above says so." },
        ai_drafts: { title: "Only replies written by the AI", body: "Replies that started from an AI draft, and AI automatic sends, wait for approval. Replies a person writes from scratch go out with one click." },
        all: { title: "Every reply", body: "Every reply from the inbox waits for approval — also the AI's automatic sends." },
      },
      fourEyes: {
        legend: "Four-eyes principle",
        help: "The person who approves must be someone other than the person who wrote the reply.",
        off: "Off",
        on: "On",
      },
      summary: {
        title: "What happens now",
        aiSends: "The AI sends low-risk replies by itself. Other replies are drafts a person sends.",
        aiSendsViaApproval: "Low-risk AI replies are not sent straight away: they wait for approval first.",
        aiDrafts: "The AI prepares a draft for incoming e-mail; a person sends it.",
        aiSuggests: "The AI only suggests a reply; a person writes and sends it.",
        aiOff: "The AI does nothing with incoming e-mail.",
        limits: "It only sends at {confidence} certainty or more, at most {count}× per conversation per day.",
        limitsWithKb: "It only sends at {confidence} certainty or more, with a knowledge-base source, at most {count}× per conversation per day.",
        approvalOff: "A reply written by a person goes out as soon as they click Send — no approval.",
        approvalAi: "Replies written by the AI wait for approval; replies written by a person go out with one click.",
        approvalAll: "Every reply waits for approval before it is sent.",
        approvers: "Approvers are the organization's admins and managers. They get a notification and an e-mail with approve and reject buttons (to their approval address when they set one in their account). When this project has a Slack or Teams channel for approvals, the reply also appears there with the same buttons; approving from chat needs an elevated account link.",
        fourEyesOn: "The writer cannot approve their own reply.",
        fourEyesOff: "An admin or manager can approve a reply they wrote themselves.",
      },
    },
    source: {
      inheritTitle: "Using {org} defaults",
      inheritBody: "This project follows the organization's style. When the defaults change, this project changes with them.",
      customTitle: "Custom for {project}",
      customBody: "This project has its own style. Changes to the {org} defaults don't reach it.",
      partialTitle: "Partly custom for {project}",
      partialBody: "Some settings come from the {org} defaults. Saving here makes this project's style complete and independent.",
      customize: "Customize for {project}",
      reset: "Reset to {org} defaults",
      resetConfirmTitle: "Reset to {org} defaults?",
      resetConfirmBody: "{project} will use the organization's style again. This project's own greeting, closing, signature and writing style are removed. Templates are not affected.",
      resetConfirm: "Reset",
      editOrgDefaults: "Change organization defaults",
      readOnly: "You can view this style. Changing it needs project admin rights.",
      orgFallbackName: "organization",
      summaryChannels: "{channels}",
      summarySignature: "Signature: {first}",
      noChannels: "No channels",
      off: "Styling off",
    },
    off: {
      title: "Replies go out exactly as written",
      body: "No greeting, closing or signature is added. Turn on “Add greeting, closing and signature” to style replies.",
    },
    orgOverrides: {
      none: "Every project uses these defaults.",
      some: "{count} project(s) use their own style, so changes here don't reach them:",
    },
    orgAdvanced: {
      title: "Personal and mailbox signatures",
      body: "A person or a shared mailbox can have its own signature on top of the active style — for example a mailbox signature that was set up before styles existed.",
    },
    style: {
      enabledLabel: "Add greeting, closing and signature",
      enabledHint: "When off, replies are sent exactly as they are written.",
      writingStyle: "Writing style",
      writingStyleHint: "Steers how AI-written drafts sound. People can still edit every word.",
      presets: {
        professional: { label: "Professional", hint: "Clear, polite and to the point." },
        friendly: { label: "Warm and helpful", hint: "Friendly, empathetic, reassuring." },
        concise: { label: "Concise and direct", hint: "Short answers, no filler." },
        technical: { label: "Technical", hint: "Precise detail for technical readers." },
        formal: { label: "Formal", hint: "Courteous and formal wording." },
        custom: { label: "Custom", hint: "Your own instructions." },
      },
      instructions: "Instructions for AI drafts",
      instructionsOptional: "Instructions for AI drafts (optional)",
      instructionsHint: "Used when AI writes a draft. Never added to the message itself.",
      instructionsPlaceholder: "Write clearly and directly. Keep paragraphs short. Avoid exaggerated apologies. Explain technical problems in simple language.",
      instructionsRequired: "Describe the writing style you want.",
      linesLanguage: "Language of the greeting, closing and chat line",
      linesAll: "All languages",
      linesEn: "English",
      linesNl: "Dutch",
      linesHint: "Replies are matched to the language they are written in. Fill in an English or Dutch version to use it for replies in that language; an empty line uses “All languages”.",
      linesHasText: "has its own text",
      greeting: "Greeting",
      greetingHint: "Opens e-mail and ticket replies. Skipped when the reply already starts with a greeting.",
      greetingPlaceholder: "Hi [Customer first name],",
      closing: "Closing",
      closingHint: "One line, such as \"Kind regards,\". It sits right above the signature, so put your name at the top of the signature to get it on the next line. Skipped when the reply already ends with one.",
      closingPlaceholder: "Kind regards,",
      signature: "Signature",
      signatureHint: "Added under e-mail and ticket replies.",
      layout: "E-mail layout",
      layoutHint: "How the e-mail itself looks. Applies to e-mail replies only.",
      layoutPlain: "Plain",
      layoutPlainHint: "Looks like an ordinary e-mail — no banner.",
      layoutBranded: "Your branding",
      layoutBrandedHint: "Your name, colour and logo at the top.",
      layoutBrandName: "Name in the header",
      layoutBrandNamePlaceholder: "BizzCloud Support",
      layoutColor: "Header colour",
      layoutColorInvalid: "Use a colour like #0f766e.",
      layoutLogo: "Logo",
      layoutLogoAdd: "Upload logo",
      layoutLogoRemove: "Remove",
      layoutLogoAlign: "Logo position",
      layoutLogoAlignLeft: "Left",
      layoutLogoAlignCenter: "Center",
      layoutLogoAlignRight: "Right",
      layoutLogoUrl: "Image address (https)",
      layoutFooter: "Footer text (optional)",
      layoutFooterPlaceholder: "BizzCloud B.V. · Amsterdam",
      senderLine: "Chat sender line",
      senderLineHint: "Teams, Slack and website chat get this one line instead of a greeting, closing and signature.",
      senderLinePlaceholder: "— [Agent name], [Your organization] Support",
      channels: "Channels",
      channelsHint: "Where this style is added. Channels that are off get the reply exactly as written.",
      connected: "Connected to this project",
      saveBar: "Unsaved changes",
      discard: "Discard",
      save: "Save",
      saving: "Saving…",
      saved: "Saved",
      saveError: "Could not save. Check the highlighted fields and try again.",
      leaveWarning: "You have unsaved changes.",
      logoTooLarge: "The logo is too large (max 256 KB).",
      logoUploadError: "The logo could not be uploaded.",
    },
    channels: {
      email: "E-mail",
      freshdesk: "Freshdesk",
      odoo: "Odoo",
      topdesk: "TOPdesk",
      jsm: "Jira Service Management",
      slack: "Slack",
      teams: "Teams",
      website_chat: "Website chat",
    },
    variables: {
      customer_first_name: "Customer first name",
      customer_name: "Customer full name",
      agent_name: "Agent name",
      agent_role: "Agent role",
      ticket: "Issue key",
      status: "Issue status",
      ticket_url: "Issue link",
      company_name: "Your organization",
      project: "Project name",
    },
    variableGroups: {
      customer: "Customer",
      agent: "Agent",
      issue: "Issue",
      organization: "Organization",
    },
    editor: {
      insertVariable: "Insert variable",
      toolbar: "Formatting",
    },
    preview: {
      title: "Live preview",
      subtitle: "What a customer receives, rendered by the same code that sends it.",
      region: "Message preview",
      channelTabs: "Preview channel",
      language: "Preview language",
      device: "Preview width",
      desktop: "Desktop",
      mobile: "Mobile",
      editData: "Edit preview data",
      content: "Preview message",
      sampleOption: "Sample reply",
      from: "From",
      to: "To",
      subject: "Subject",
      newConversationSubject: "New conversation",
      projectMailbox: "the mailbox linked to this project",
      orgSender: "{org} (your organization's outgoing address)",
      sampleSubject: "Re: Login page shows an error",
      sampleBody: "Good news — the issue you reported has been resolved.\n\nCould you check whether everything works as expected on your side?",
      trackerHeading: "Public reply on {ticket}",
      chatHeading: "Reply in the conversation",
      chatNote: "Chat replies carry one sender line instead of a greeting, closing and signature.",
      plainNote: "This channel shows replies as plain text.",
      channelOff: "Styling is off for {channel} — the reply goes out exactly as written.",
      styleOff: "Styling is off — the reply goes out exactly as written.",
      missing: "{name} is not available in this preview, so it is left out.",
      unknownVariable: "This text uses a variable that does not exist. Remove it before saving.",
      frameTitle: "E-mail preview, {width}",
      noChannel: "Connect a channel to this project to preview it here.",
    },
    previewData: {
      title: "Preview data",
      sub: "Example values for the preview and the test message. Real replies use the customer's and issue's own values.",
      customer_first_name: "Customer first name",
      customer_name: "Customer full name",
      agent_name: "Agent name",
      agent_role: "Agent role",
      company_name: "Your organization",
      ticket: "Issue key",
      status: "Issue status",
      project: "Project name",
      exampleRole: "Support engineer",
      exampleStatus: "Resolved",
      agentNote: "Test messages always use your own name as the agent.",
      reset: "Reset to examples",
      done: "Done",
    },
    test: {
      send: "Send test message to me",
      sending: "Sending…",
      saveFirst: "Save your changes first — the test uses the saved settings.",
      emailOnly: "Test messages are sent as e-mail.",
      sent: "Test sent to {email}. Check your inbox.",
      sentAsMailbox: "It went out as {from} — the same sender your customers see.",
      queuedAsMailbox: "It is on its way from {from} — the same sender your customers see. It can take a minute to arrive.",
      sentViaOrgMailbox: "It went out through your organization's outgoing e-mail. Replies to customers come from {mailbox}.",
      sentViaOrg: "It went out through your organization's outgoing e-mail, the same way replies to customers do.",
      mailboxFailed: "Sending as the project's mailbox did not work ({reason}), so the test used your organization's outgoing e-mail.",
      failed: "The test could not be sent ({reason}).",
      rateLimited: "Too many test messages. Try again in a few minutes.",
      held: "Heads-up: the safety check would hold this reply before it reaches a customer ({reasons}).",
      fromProject: "Send a test message from a project's Customer communication page — it uses that project's mailbox and style.",
    },
    templates: {
      intro: "Templates are reusable message content. The style above adds the greeting, closing, signature and channel layout around them.",
      new: "New template",
      empty: "No templates yet. Templates are optional — they give your team a head start on common replies.",
      orgEmpty: "No organization templates yet.",
      available: "Available in composer",
      off: "Off",
      languages: "EN + NL",
      scopeOrganization: "Organization template",
      scopeProject: "This project",
      scopeProjectVersion: "This project's version",
      replacedHere: "Replaced in this project",
      edit: "Edit",
      duplicate: "Duplicate",
      disable: "Turn off",
      enable: "Turn on",
      delete: "Delete",
      customize: "Make a version for this project",
      previewIt: "Preview",
      deleteConfirmTitle: "Delete “{name}”?",
      deleteConfirmBody: "The template is removed from this project. Replies already sent are not affected.",
      deleteConfirmBodyOrg: "The template is removed for every project. Projects that made their own version keep it. Replies already sent are not affected.",
      orgEditHint: "Organization templates are edited in organization settings.",
      actionsFor: "Actions for {name}",
      error: "That did not work. Try again.",
      nameTaken: "A template with this name already exists in this project.",
      categories: {
        confirmation: "Received",
        resolution: "Resolved",
        escalation: "Needs more information",
        closing: "Closing",
        general: "General",
      },
    },
    templateEditor: {
      newTitle: "New template",
      editTitle: "Edit template",
      name: "Name",
      namePlaceholder: "Problem resolved",
      category: "Use case",
      scope: "Available in",
      scopeProjectValue: "{project} only",
      scopeOrgValue: "All projects",
      languageTabs: "Template language",
      english: "English",
      dutch: "Nederlands",
      body: "Message",
      bodyHint: "Just the content — the greeting, closing and signature come from the style.",
      enabled: "Available in composer",
      enabledHint: "When off, the template stays here but is not offered while replying.",
      missingLanguage: "Add the {language} version too. Customers get the language they wrote in.",
      nameRequired: "Give the template a name.",
      cancel: "Cancel",
      save: "Save template",
      saving: "Saving…",
      previewHeading: "Template + style = what the customer gets",
      languageNames: { en: "English", nl: "Dutch" },
      translate: "Generate from {language}",
      translating: "Translating…",
      replaceConfirm: "Replace the current {language} text with a new translation?",
      replace: "Replace",
      generated: "Translated by AI from the {language} version — read it before you save.",
      translateUnavailable: "Translation is not available: no AI provider is set up for your organization.",
      translateFailed: "The translation did not keep the variables or formatting intact, so it was not used. Try again.",
      translateRateLimited: "Too many translations in a short time. Try again in a few minutes.",
      translateEmpty: "Write the {language} version first.",
      subject: "E-mail subject for a new conversation",
      subjectHint: "Optional. Used only when this reply starts a new e-mail conversation; a reply to an existing e-mail keeps its subject (Re: …).",
    },
  },
};

export const customerCommunicationNl: typeof customerCommunicationEn = {
  customerCommunication: {
    title: "Klantcommunicatie",
    subtitle: "Hoe antwoorden aan klanten klinken, eruitzien en ondertekend worden — op elk kanaal.",
    orgTitle: "Standaard klantcommunicatie",
    orgSubtitle: "De stijl die elk project gebruikt, tenzij het een eigen stijl heeft. Projecten die aanpassen houden hun eigen kopie.",
    loading: "Klantcommunicatie laden…",
    loadError: "De instellingen voor klantcommunicatie konden niet worden geladen.",
    retry: "Opnieuw proberen",
    tabs: {
      label: "Onderdelen van klantcommunicatie",
      style: "Stijl & afzender",
      templates: "Templates",
      preview: "Voorbeeld & test",
      sending: "Verzenden & goedkeuring",
    },
    sending: {
      inherit: "Zoals de organisatie",
      inheritValue: "Zoals de organisatie ({value})",
      saved: "Opgeslagen.",
      saveError: "Opslaan mislukt. Probeer het opnieuw.",
      saveBar: "Niet-opgeslagen wijzigingen in verzenden & goedkeuring",
      readOnly: "Alleen projectbeheerders kunnen dit wijzigen.",
      orgHint: "Projecten volgen deze instellingen, tenzij ze op hun pagina Goedkeuringen & regels iets anders kiezen.",
      summaryLine: { replies: "Antwoorden:", approval: "Goedkeuring:", manage: "Instellen bij Goedkeuringen & regels" },
      ai: {
        legend: "Wanneer mag de AI zelf een antwoord versturen?",
        help: "Wat de AI doet met een binnenkomende klantmail in de inbox.",
        off: { title: "Uit", body: "De AI doet niets met binnenkomende mail." },
        suggest_only: { title: "Alleen suggesties", body: "De AI stelt een antwoord voor in de inbox. Een mens schrijft en verstuurt." },
        auto_draft: { title: "Concepten klaarzetten", body: "De AI zet een conceptantwoord klaar. Een mens controleert en verstuurt het." },
        auto_send_low_risk: { title: "Automatisch versturen bij laag risico", body: "De AI verstuurt het antwoord zelf als hij zeker genoeg is en het risico laag is, binnen de grenzen hieronder. Al het andere wordt een concept." },
      },
      limits: {
        legend: "Grenzen voor automatisch versturen",
        help: "Buiten deze grenzen verstuurt de AI niets: het antwoord wordt een concept dat een mens controleert.",
        confidence: { label: "Minimale zekerheid", help: "Hoe zeker de AI van zijn antwoord moet zijn voordat hij het zelf verstuurt." },
        kb: { label: "Bron uit de kennisbank vereist", help: "Alleen zelf versturen als het antwoord steunt op een artikel uit de kennisbank." },
        max: { label: "Automatische antwoorden per gesprek per dag", help: "Daarna wordt elk volgend antwoord in dat gesprek een concept." },
        yes: "Ja",
        no: "Nee",
        never: "De AI beantwoordt alleen vragen en hoe-doe-ik-vragen zelf. Klachten, bugs, wensen, terugbetalingen, factuurgeschillen, juridische en beveiligingsvragen, boze klanten en iedereen die om een mens vraagt krijgen altijd een concept.",
      },
      approval: {
        legend: "Moet een antwoord eerst worden goedgekeurd?",
        help: "Een goedkeuring houdt het antwoord tegen tot een beheerder of manager het goedkeurt: in FixControl, vanuit de e-mail die ze krijgen, of in het Slack- of Teams-goedkeuringskanaal van het project als dat er is.",
        off: { title: "Nee", body: "Wie het antwoord schrijft, verstuurt het met één klik. Er gaat niets automatisch de deur uit, tenzij de AI-instelling hierboven dat toestaat." },
        ai_drafts: { title: "Alleen antwoorden van de AI", body: "Antwoorden die begonnen als AI-concept, en automatische verzendingen van de AI, wachten op goedkeuring. Antwoorden die een mens zelf schrijft gaan met één klik." },
        all: { title: "Elk antwoord", body: "Elk antwoord uit de inbox wacht op goedkeuring — ook de automatische verzendingen van de AI." },
      },
      fourEyes: {
        legend: "Vier-ogenprincipe",
        help: "Wie goedkeurt moet iemand anders zijn dan wie het antwoord schreef.",
        off: "Uit",
        on: "Aan",
      },
      summary: {
        title: "Wat er nu gebeurt",
        aiSends: "De AI verstuurt antwoorden met laag risico zelf. Andere antwoorden zijn concepten die een mens verstuurt.",
        aiSendsViaApproval: "AI-antwoorden met laag risico gaan niet meteen weg: ze wachten eerst op goedkeuring.",
        aiDrafts: "De AI zet een concept klaar voor binnenkomende mail; een mens verstuurt het.",
        aiSuggests: "De AI stelt alleen een antwoord voor; een mens schrijft en verstuurt het.",
        aiOff: "De AI doet niets met binnenkomende mail.",
        limits: "Hij verstuurt alleen bij minstens {confidence} zekerheid, hooguit {count}× per gesprek per dag.",
        limitsWithKb: "Hij verstuurt alleen bij minstens {confidence} zekerheid, met een bron uit de kennisbank, hooguit {count}× per gesprek per dag.",
        approvalOff: "Een antwoord dat een mens schrijft, gaat weg zodra die op Verzenden klikt — zonder goedkeuring.",
        approvalAi: "Antwoorden van de AI wachten op goedkeuring; antwoorden die een mens schrijft gaan met één klik.",
        approvalAll: "Elk antwoord wacht op goedkeuring voordat het verstuurd wordt.",
        approvers: "Goedkeurders zijn de beheerders en managers van de organisatie. Ze krijgen een melding en een e-mail met knoppen om goed te keuren of af te wijzen (naar hun goedkeuringsadres als ze dat in hun account hebben ingesteld). Heeft dit project een Slack- of Teams-kanaal voor goedkeuringen, dan verschijnt het antwoord daar ook, met dezelfde knoppen; goedkeuren vanuit de chat vraagt een verhoogde accountkoppeling.",
        fourEyesOn: "Wie het antwoord schreef, kan het niet zelf goedkeuren.",
        fourEyesOff: "Een beheerder of manager mag ook een eigen antwoord goedkeuren.",
      },
    },
    source: {
      inheritTitle: "Gebruikt de standaard van {org}",
      inheritBody: "Dit project volgt de stijl van de organisatie. Verandert de standaard, dan verandert dit project mee.",
      customTitle: "Eigen stijl voor {project}",
      customBody: "Dit project heeft een eigen stijl. Wijzigingen in de standaard van {org} komen hier niet aan.",
      partialTitle: "Deels eigen stijl voor {project}",
      partialBody: "Een deel komt nog uit de standaard van {org}. Opslaan maakt de stijl van dit project compleet en zelfstandig.",
      customize: "Aanpassen voor {project}",
      reset: "Terug naar de standaard van {org}",
      resetConfirmTitle: "Terug naar de standaard van {org}?",
      resetConfirmBody: "{project} gebruikt dan weer de stijl van de organisatie. De eigen aanhef, afsluiting, handtekening en schrijfstijl van dit project verdwijnen. Templates blijven staan.",
      resetConfirm: "Terugzetten",
      editOrgDefaults: "Standaard van de organisatie wijzigen",
      readOnly: "Je kunt deze stijl bekijken. Wijzigen vraagt projectbeheerrechten.",
      orgFallbackName: "de organisatie",
      summaryChannels: "{channels}",
      summarySignature: "Handtekening: {first}",
      noChannels: "Geen kanalen",
      off: "Opmaak uit",
    },
    off: {
      title: "Antwoorden gaan precies zo de deur uit als ze geschreven zijn",
      body: "Er komt geen aanhef, afsluiting of handtekening bij. Zet “Aanhef, afsluiting en handtekening toevoegen” aan om antwoorden op te maken.",
    },
    orgOverrides: {
      none: "Alle projecten gebruiken deze standaard.",
      some: "{count} project(en) hebben een eigen stijl; wijzigingen hier komen daar niet aan:",
    },
    orgAdvanced: {
      title: "Persoonlijke handtekeningen en mailboxhandtekeningen",
      body: "Een persoon of een gedeelde mailbox kan een eigen handtekening hebben bovenop de actieve stijl — bijvoorbeeld een mailboxhandtekening die er al was voordat stijlen bestonden.",
    },
    style: {
      enabledLabel: "Aanhef, afsluiting en handtekening toevoegen",
      enabledHint: "Staat dit uit, dan gaan antwoorden precies zo weg als ze geschreven zijn.",
      writingStyle: "Schrijfstijl",
      writingStyleHint: "Bepaalt hoe door AI geschreven concepten klinken. Mensen kunnen elk woord nog aanpassen.",
      presets: {
        professional: { label: "Professioneel", hint: "Helder, beleefd en to the point." },
        friendly: { label: "Warm en behulpzaam", hint: "Vriendelijk, empathisch, geruststellend." },
        concise: { label: "Beknopt en direct", hint: "Korte antwoorden, zonder omhaal." },
        technical: { label: "Technisch", hint: "Precieze details voor technische lezers." },
        formal: { label: "Formeel", hint: "Hoffelijk en formeel geformuleerd." },
        custom: { label: "Eigen stijl", hint: "Je eigen instructies." },
      },
      instructions: "Instructies voor AI-concepten",
      instructionsOptional: "Instructies voor AI-concepten (optioneel)",
      instructionsHint: "Gebruikt als AI een concept schrijft. Komt nooit in het bericht zelf.",
      instructionsPlaceholder: "Schrijf helder en direct. Houd alinea's kort. Geen overdreven excuses. Leg technische problemen in eenvoudige taal uit.",
      instructionsRequired: "Beschrijf de schrijfstijl die je wilt.",
      linesLanguage: "Taal van de aanhef, afsluiting en chatregel",
      linesAll: "Alle talen",
      linesEn: "Engels",
      linesNl: "Nederlands",
      linesHint: "Antwoorden krijgen de taal waarin ze geschreven zijn. Vul een Engelse of Nederlandse versie in om die te gebruiken voor antwoorden in die taal; een lege regel gebruikt “Alle talen”.",
      linesHasText: "heeft eigen tekst",
      greeting: "Aanhef",
      greetingHint: "Opent antwoorden per e-mail en ticket. Wordt overgeslagen als het antwoord al met een groet begint.",
      greetingPlaceholder: "Hoi [Voornaam klant],",
      closing: "Afsluiting",
      closingHint: "Eén regel, zoals \"Met vriendelijke groet,\". Hij staat direct boven de handtekening: zet je naam bovenaan de handtekening, dan komt die op de regel eronder. Wordt overgeslagen als het antwoord er al mee eindigt.",
      closingPlaceholder: "Met vriendelijke groet,",
      signature: "Handtekening",
      signatureHint: "Komt onder antwoorden per e-mail en ticket.",
      layout: "Opmaak van de e-mail",
      layoutHint: "Hoe de e-mail zelf eruitziet. Geldt alleen voor antwoorden per e-mail.",
      layoutPlain: "Eenvoudig",
      layoutPlainHint: "Ziet eruit als een gewone e-mail — zonder banner.",
      layoutBranded: "Je eigen huisstijl",
      layoutBrandedHint: "Je naam, kleur en logo bovenaan.",
      layoutBrandName: "Naam in de kop",
      layoutBrandNamePlaceholder: "BizzCloud Support",
      layoutColor: "Kleur van de kop",
      layoutColorInvalid: "Gebruik een kleur zoals #0f766e.",
      layoutLogo: "Logo",
      layoutLogoAdd: "Logo uploaden",
      layoutLogoRemove: "Verwijderen",
      layoutLogoAlign: "Plaats van het logo",
      layoutLogoAlignLeft: "Links",
      layoutLogoAlignCenter: "Midden",
      layoutLogoAlignRight: "Rechts",
      layoutLogoUrl: "Adres van de afbeelding (https)",
      layoutFooter: "Voettekst (optioneel)",
      layoutFooterPlaceholder: "BizzCloud B.V. · Amsterdam",
      senderLine: "Afzenderregel in chat",
      senderLineHint: "Teams, Slack en websitechat krijgen deze ene regel in plaats van aanhef, afsluiting en handtekening.",
      senderLinePlaceholder: "— [Naam medewerker], [Je organisatie] Support",
      channels: "Kanalen",
      channelsHint: "Waar deze stijl wordt toegevoegd. Kanalen die uit staan krijgen het antwoord precies zoals het geschreven is.",
      connected: "Gekoppeld aan dit project",
      saveBar: "Niet-opgeslagen wijzigingen",
      discard: "Annuleren",
      save: "Opslaan",
      saving: "Opslaan…",
      saved: "Opgeslagen",
      saveError: "Opslaan lukte niet. Controleer de gemarkeerde velden en probeer het opnieuw.",
      leaveWarning: "Je hebt niet-opgeslagen wijzigingen.",
      logoTooLarge: "Het logo is te groot (max. 256 KB).",
      logoUploadError: "Het logo kon niet worden geüpload.",
    },
    channels: {
      email: "E-mail",
      freshdesk: "Freshdesk",
      odoo: "Odoo",
      topdesk: "TOPdesk",
      jsm: "Jira Service Management",
      slack: "Slack",
      teams: "Teams",
      website_chat: "Websitechat",
    },
    variables: {
      customer_first_name: "Voornaam klant",
      customer_name: "Volledige naam klant",
      agent_name: "Naam medewerker",
      agent_role: "Rol medewerker",
      ticket: "Issuenummer",
      status: "Status issue",
      ticket_url: "Link naar issue",
      company_name: "Je organisatie",
      project: "Projectnaam",
    },
    variableGroups: {
      customer: "Klant",
      agent: "Medewerker",
      issue: "Issue",
      organization: "Organisatie",
    },
    editor: {
      insertVariable: "Variabele invoegen",
      toolbar: "Opmaak",
    },
    preview: {
      title: "Live voorbeeld",
      subtitle: "Wat een klant ontvangt, opgebouwd door dezelfde code die het verstuurt.",
      region: "Voorbeeld van het bericht",
      channelTabs: "Kanaal van het voorbeeld",
      language: "Taal van het voorbeeld",
      device: "Breedte van het voorbeeld",
      desktop: "Desktop",
      mobile: "Mobiel",
      editData: "Voorbeeldgegevens wijzigen",
      content: "Voorbeeldbericht",
      sampleOption: "Voorbeeldantwoord",
      from: "Van",
      to: "Aan",
      subject: "Onderwerp",
      newConversationSubject: "Nieuw gesprek",
      projectMailbox: "de mailbox die aan dit project gekoppeld is",
      orgSender: "{org} (het uitgaande adres van je organisatie)",
      sampleSubject: "Re: Inlogpagina geeft een foutmelding",
      sampleBody: "Goed nieuws — het probleem dat je meldde is opgelost.\n\nWil je controleren of alles bij jou weer werkt zoals verwacht?",
      trackerHeading: "Openbaar antwoord op {ticket}",
      chatHeading: "Antwoord in het gesprek",
      chatNote: "Antwoorden in chat krijgen één afzenderregel in plaats van aanhef, afsluiting en handtekening.",
      plainNote: "Dit kanaal toont antwoorden als platte tekst.",
      channelOff: "Opmaak staat uit voor {channel} — het antwoord gaat precies zo weg als het geschreven is.",
      styleOff: "Opmaak staat uit — het antwoord gaat precies zo weg als het geschreven is.",
      missing: "{name} is in dit voorbeeld niet beschikbaar en wordt weggelaten.",
      unknownVariable: "Deze tekst gebruikt een variabele die niet bestaat. Haal die weg voor je opslaat.",
      frameTitle: "Voorbeeld van de e-mail, {width}",
      noChannel: "Koppel een kanaal aan dit project om het hier te bekijken.",
    },
    previewData: {
      title: "Voorbeeldgegevens",
      sub: "Voorbeeldwaarden voor het voorbeeld en het testbericht. Echte antwoorden gebruiken de gegevens van de klant en het issue.",
      customer_first_name: "Voornaam klant",
      customer_name: "Volledige naam klant",
      agent_name: "Naam medewerker",
      agent_role: "Rol medewerker",
      company_name: "Je organisatie",
      ticket: "Issuenummer",
      status: "Status issue",
      project: "Projectnaam",
      exampleRole: "Supportmedewerker",
      exampleStatus: "Opgelost",
      agentNote: "Testberichten gebruiken altijd je eigen naam als medewerker.",
      reset: "Terug naar voorbeeldwaarden",
      done: "Klaar",
    },
    test: {
      send: "Stuur mij een testbericht",
      sending: "Versturen…",
      saveFirst: "Sla je wijzigingen eerst op — de test gebruikt de opgeslagen instellingen.",
      emailOnly: "Testberichten worden als e-mail verstuurd.",
      sent: "Test verstuurd naar {email}. Kijk in je inbox.",
      sentAsMailbox: "Hij ging weg als {from} — dezelfde afzender die je klanten zien.",
      queuedAsMailbox: "Hij is onderweg vanaf {from} — dezelfde afzender die je klanten zien. Het kan een minuut duren.",
      sentViaOrgMailbox: "Hij ging via de uitgaande e-mail van je organisatie. Antwoorden aan klanten komen van {mailbox}.",
      sentViaOrg: "Hij ging via de uitgaande e-mail van je organisatie, net als antwoorden aan klanten.",
      mailboxFailed: "Versturen als de mailbox van het project lukte niet ({reason}), dus de test ging via de uitgaande e-mail van je organisatie.",
      failed: "De test kon niet worden verstuurd ({reason}).",
      rateLimited: "Te veel testberichten. Probeer het over een paar minuten opnieuw.",
      held: "Let op: de veiligheidscontrole zou dit antwoord tegenhouden voordat het een klant bereikt ({reasons}).",
      fromProject: "Stuur een testbericht vanaf de pagina Klantcommunicatie van een project — die gebruikt de mailbox en stijl van dat project.",
    },
    templates: {
      intro: "Templates zijn herbruikbare berichtinhoud. De stijl hierboven voegt de aanhef, afsluiting, handtekening en kanaalopmaak eromheen toe.",
      new: "Nieuw template",
      empty: "Nog geen templates. Templates zijn optioneel — ze geven je team een vliegende start bij veelvoorkomende antwoorden.",
      orgEmpty: "Nog geen templates voor de organisatie.",
      available: "Beschikbaar bij antwoorden",
      off: "Uit",
      languages: "EN + NL",
      scopeOrganization: "Template van de organisatie",
      scopeProject: "Dit project",
      scopeProjectVersion: "Eigen versie van dit project",
      replacedHere: "Vervangen in dit project",
      edit: "Bewerken",
      duplicate: "Dupliceren",
      disable: "Uitzetten",
      enable: "Aanzetten",
      delete: "Verwijderen",
      customize: "Eigen versie voor dit project maken",
      previewIt: "Voorbeeld",
      deleteConfirmTitle: "“{name}” verwijderen?",
      deleteConfirmBody: "Het template verdwijnt uit dit project. Verstuurde antwoorden blijven ongewijzigd.",
      deleteConfirmBodyOrg: "Het template verdwijnt voor alle projecten. Projecten met een eigen versie houden die. Verstuurde antwoorden blijven ongewijzigd.",
      orgEditHint: "Templates van de organisatie bewerk je in de organisatie-instellingen.",
      actionsFor: "Acties voor {name}",
      error: "Dat lukte niet. Probeer het opnieuw.",
      nameTaken: "Er bestaat in dit project al een template met deze naam.",
      categories: {
        confirmation: "Ontvangen",
        resolution: "Opgelost",
        escalation: "Meer informatie nodig",
        closing: "Afsluiten",
        general: "Algemeen",
      },
    },
    templateEditor: {
      newTitle: "Nieuw template",
      editTitle: "Template bewerken",
      name: "Naam",
      namePlaceholder: "Probleem opgelost",
      category: "Toepassing",
      scope: "Beschikbaar in",
      scopeProjectValue: "Alleen {project}",
      scopeOrgValue: "Alle projecten",
      languageTabs: "Taal van het template",
      english: "English",
      dutch: "Nederlands",
      body: "Bericht",
      bodyHint: "Alleen de inhoud — aanhef, afsluiting en handtekening komen uit de stijl.",
      enabled: "Beschikbaar bij antwoorden",
      enabledHint: "Staat dit uit, dan blijft het template hier staan maar wordt het niet aangeboden bij het antwoorden.",
      missingLanguage: "Voeg ook de {language} versie toe. Klanten krijgen de taal waarin ze schreven.",
      nameRequired: "Geef het template een naam.",
      cancel: "Annuleren",
      save: "Template opslaan",
      saving: "Opslaan…",
      previewHeading: "Template + stijl = wat de klant krijgt",
      languageNames: { en: "Engels", nl: "Nederlands" },
      translate: "Genereren vanuit het {language}",
      translating: "Vertalen…",
      replaceConfirm: "De huidige tekst in het {language} vervangen door een nieuwe vertaling?",
      replace: "Vervangen",
      generated: "Door AI vertaald vanuit de versie in het {language} — lees hem na voordat je opslaat.",
      translateUnavailable: "Vertalen is niet beschikbaar: er is geen AI-provider ingesteld voor je organisatie.",
      translateFailed: "De vertaling hield de variabelen of opmaak niet intact en is daarom niet gebruikt. Probeer het opnieuw.",
      translateRateLimited: "Te veel vertalingen in korte tijd. Probeer het over een paar minuten opnieuw.",
      translateEmpty: "Schrijf eerst de versie in het {language}.",
      subject: "Onderwerp bij een nieuw e-mailgesprek",
      subjectHint: "Optioneel. Alleen gebruikt als dit antwoord een nieuw e-mailgesprek begint; een antwoord op een bestaande e-mail houdt het onderwerp daarvan (Re: …).",
    },
  },
};
